import { AGENT_URL } from '@/config/env'
import { fetchJsonWithAuth, mutateJsonWithAuth } from '@/lib/api'
import type { CapabilityGrant } from './grants'

/** Stored grants for the caller's active org, plus which layer each row came from. */
export interface PermissionGrantsResponse {
  scope: string
  grants: Record<string, CapabilityGrant>
  /**
   * Capability ids some handler guards. A row outside this decides nothing, so
   * the matrix must not offer it: saving a restriction there would report
   * success and change no request.
   */
  enforced: string[]
  /**
   * Every organization that exists, for the column that moves a capability
   * between them. Present on the product-wide scope alone, which is the only
   * one with that column.
   */
  orgs?: string[]
}

export interface SaveGrantsResponse {
  updated: number
  scope?: string
}

/** Capability ids the signed-in user's own role may use. */
export interface MyCapabilitiesResponse {
  capabilities: string[]
}

export function fetchMyCapabilities(
  token: string,
  signal?: AbortSignal
): Promise<MyCapabilitiesResponse> {
  return fetchJsonWithAuth<MyCapabilitiesResponse>({
    token,
    url: `${AGENT_URL}/permissions/me`,
    signal,
    errorMessage: 'Failed to load your permissions',
  })
}

export function fetchPermissionGrants(
  token: string,
  signal?: AbortSignal
): Promise<PermissionGrantsResponse> {
  return fetchJsonWithAuth<PermissionGrantsResponse>({
    token,
    url: `${AGENT_URL}/permissions`,
    signal,
    errorMessage: 'Failed to load permissions',
  })
}

export function savePermissionGrants(
  token: string,
  grants: Record<string, CapabilityGrant>
): Promise<SaveGrantsResponse> {
  return mutateJsonWithAuth<SaveGrantsResponse>({
    token,
    url: `${AGENT_URL}/permissions`,
    method: 'PATCH',
    body: { grants },
    errorMessage: 'Failed to save permissions',
  })
}
